import { Check, Loader2, X } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

export interface BulkUploadResponse {
  recordCount?: number;
  error?: string;
  filenames?: string[];
}

interface BulkFileUploadProps {
  instructions?: string;
  accept?: string;
  uploadButtonText?: string;
  onUpload: (files: File[]) => Promise<BulkUploadResponse>;
  onUploadComplete?: (response: BulkUploadResponse) => void;
  onFileSelect?: (files: File[] | null) => void;
  recordCount?: number | null;
  setRecordCount: (count: number | null) => void;
  onReset?: () => void;
  error?: string | null;
  maxFiles?: number;
  maxSizeMB?: number;
}

export const BulkFileUpload: React.FC<BulkFileUploadProps> = ({
  instructions = 'Upload files',
  accept = '*/*',
  uploadButtonText = 'Upload Files',
  onUpload,
  onUploadComplete,
  onFileSelect,
  recordCount,
  setRecordCount,
  onReset,
  error: externalError,
  maxFiles = 20,
  maxSizeMB = 100,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Use external error if provided, otherwise use internal error
  const error = externalError ?? internalError;

  const validateFiles = (fileList: File[]): string | null => {
    if (fileList.length === 0) {
      return 'Please select at least one file.';
    }

    if (fileList.length > maxFiles) {
      return `Maximum ${maxFiles} files allowed. You selected ${fileList.length} files.`;
    }

    const totalSize = fileList.reduce((sum, file) => sum + file.size, 0);
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (totalSize > maxSizeBytes) {
      return `Total file size exceeds ${maxSizeMB}MB limit. Current size: ${(totalSize / 1024 / 1024).toFixed(1)}MB`;
    }

    // Check file types
    const invalidFiles = fileList.filter((file) => !file.type.includes('pdf'));
    if (invalidFiles.length > 0) {
      return `Invalid file types. Only PDF files are allowed. Invalid files: ${invalidFiles.map((f) => f.name).join(', ')}`;
    }

    return null;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    setFiles(selectedFiles);
    setInternalError(null);
    setRecordCount(null);

    if (onFileSelect) {
      onFileSelect(selectedFiles.length > 0 ? selectedFiles : null);
    }

    // Validate files
    const validationError = validateFiles(selectedFiles);
    if (validationError) {
      setInternalError(validationError);
      return;
    }

    // Automatically trigger upload if files are selected
    if (selectedFiles.length > 0) {
      await handleUpload(selectedFiles);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    const selectedFiles = Array.from(event.dataTransfer.files || []);
    setFiles(selectedFiles);
    setInternalError(null);
    setDragOver(false);

    if (onFileSelect) {
      onFileSelect(selectedFiles.length > 0 ? selectedFiles : null);
    }

    // Validate files
    const validationError = validateFiles(selectedFiles);
    if (validationError) {
      setInternalError(validationError);
      return;
    }

    // Automatically trigger upload if files are dropped
    if (selectedFiles.length > 0) {
      await handleUpload(selectedFiles);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    setDragOver(false);
  };

  const handleUpload = async (filesToUpload: File[]) => {
    try {
      setLoading(true);
      const response = await onUpload(filesToUpload);
      if (onUploadComplete) {
        onUploadComplete(response);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setInternalError(
        err instanceof Error ? err.message : 'An error occurred during upload.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setInternalError(null);
    setRecordCount(null);
    if (onFileSelect) {
      onFileSelect(null);
    }
    if (onReset) {
      onReset();
    }
  };

  const removeFile = (indexToRemove: number) => {
    const newFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(newFiles);
    setInternalError(null);
    setRecordCount(null);

    if (onFileSelect) {
      onFileSelect(newFiles.length > 0 ? newFiles : null);
    }

    // Re-validate remaining files
    if (newFiles.length > 0) {
      const validationError = validateFiles(newFiles);
      if (validationError) {
        setInternalError(validationError);
      } else {
        // Re-upload if validation passes
        handleUpload(newFiles);
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div className="text-sm">
      <label
        htmlFor="bulk-file-upload"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`flex w-full flex-col items-center justify-center rounded-md border-2 p-8 transition-colors duration-150 ${
          dragOver
            ? 'border-foreground border-solid bg-muted'
            : 'border-input border-dashed'
        }${files.length > 0 ? ' bg-muted/50' : ''}`}
      >
        {files.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center">
            <span className="mb-2 font-normal text-sm">
              Click to upload or drag and drop multiple files here
            </span>
            <label
              htmlFor="bulk-file-upload"
              className="inline-block cursor-pointer rounded-md bg-primary px-8 py-2 font-medium text-sm text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Choose Files
            </label>
            <input
              id="bulk-file-upload"
              type="file"
              accept={accept}
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        )}

        {files.length > 0 && (
          <div className="w-full space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {loading && (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                )}
                {!loading && error && <X className="h-4 w-4 text-red-500" />}
                {!loading && !error && (
                  <Check className="h-4 w-4 text-green-500" />
                )}
                <span className="font-medium text-sm">
                  {files.length} file{files.length !== 1 ? 's' : ''} selected
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleReset();
                }}
                className="rounded-md bg-muted px-3 py-1 font-medium text-foreground text-xs hover:bg-accent"
              >
                Clear All
              </button>
            </div>

            <div className="max-h-48 space-y-2 overflow-y-auto">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between rounded-md bg-card text-card-foreground p-3 shadow-sm"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded bg-red-100 dark:bg-red-900/40">
                        <span className="font-medium text-red-600 text-xs dark:text-red-400">
                          PDF
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate font-medium text-sm"
                        title={file.name}
                      >
                        {file.name}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      removeFile(index);
                    }}
                    className="ml-2 shrink-0 rounded-full p-1 transition-colors hover:bg-muted"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Show either error or success message */}
        {files.length > 0 && (
          <div className="mt-3 text-center text-sm">
            {error ? (
              <p className="text-red-500">{error}</p>
            ) : (
              recordCount !== null && (
                <p className="text-green-500">
                  {recordCount} file{recordCount !== 1 ? 's' : ''} ready for
                  upload.
                </p>
              )
            )}
          </div>
        )}
      </label>
    </div>
  );
};
